import type { PatternSpec } from '../../core/api/types';

/**
 * The pattern in words — the one place the application turns a `PatternSpec` into English
 * (research R-009). The template row (FR-027) and the recurrence glyph's accessible label
 * (FR-032) both read through here, so the phrase a practitioner hears from a screen reader is
 * character-for-character the phrase the row shows.
 *
 * Two properties are deliberate:
 *
 * - **The names below are this file's own, not `Intl`'s.** `toLocaleDateString` would render
 *   "March 14" on a machine set to en-US and "14 March" on one set to en-GB, which would make
 *   the phrase — and its test — depend on where the machine is. The application has exactly one
 *   client and no i18n (R-009); words are presentation, and this is the presentation.
 * - **An interval of one is a word, not a number.** "every 1 weeks" is the tell of a phrase
 *   assembled by a machine, and this is the string a practitioner reads on every template row.
 */

/** ISO Monday = 1 … Sunday = 7, at every layer (spec FR-011, clarification Q1). */
export const WEEK_DAY_NAMES: readonly string[] = [
  'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday',
];

/**
 * Exported so the pattern form's controls label a weekday and a month the same way this file
 * does — one spelling of "Wednesday" in the application, not two that can drift.
 */
export const MONTH_NAMES: readonly string[] = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];

const weekDayName = (isoWeekDay: number): string => WEEK_DAY_NAMES[isoWeekDay - 1] ?? '';
const monthName = (month: number): string => MONTH_NAMES[month - 1] ?? '';

/** "Monday", "Monday and Thursday", "Monday, Friday and Sunday" — ISO order, never the caller's. */
const namedDays = (isoWeekDays: readonly number[]): string => {
  const names = [...isoWeekDays].sort((a, b) => a - b).map(weekDayName);
  if (names.length <= 1) return names.join('');
  return `${names.slice(0, -1).join(', ')} and ${names[names.length - 1]}`;
};

/** "day"/"3 days", "week"/"2 weeks", "month"/"3 months" — the singular drops the count. */
const every = (count: number, noun: string): string =>
  count === 1 ? `every ${noun}` : `every ${count} ${noun}s`;

export const patternInWords = (pattern: PatternSpec): string => {
  switch (pattern.kind) {
    case 'daily_interval':
      return every(pattern.everyNDays, 'day');
    case 'daily_work_days':
      return 'every work day';
    case 'weekly':
      return `${every(pattern.everyNWeeks, 'week')} on ${namedDays(pattern.weekDays)}`;
    case 'monthly_day':
      return `on day ${pattern.day} of ${every(pattern.everyNMonths, 'month')}`;
    case 'monthly_ordinal':
      return `the ${pattern.ordinal} ${weekDayName(pattern.weekDay)} of ${every(pattern.everyNMonths, 'month')}`;
    case 'yearly_date':
      return `every ${pattern.day} ${monthName(pattern.month)}`;
    case 'yearly_ordinal':
      return `the ${pattern.ordinal} ${weekDayName(pattern.weekDay)} of ${monthName(pattern.month)}`;
    default:
      // The discriminator is optional in the generated schema and always present on the wire.
      // A glyph with an empty label is worse than a vague one, so say the true, small thing.
      return 'on a repeating schedule';
  }
};
